/**
 * Avatar utilities for user profile display
 * Updated for sleek black theme with emerald/cyan accent colors
 */

const AVATAR_COLORS = [
  'from-emerald-500 to-cyan-500',
  'from-cyan-500 to-blue-500',
  'from-blue-500 to-purple-500',
  'from-purple-500 to-pink-500',
  'from-emerald-500 to-green-500',
  'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-500',
  'from-teal-500 to-emerald-500',
]

/**
 * Get consistent avatar color gradient based on username
 */
export function getAvatarColor(username: string): string {
  const hash = username
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)

  const index = hash % AVATAR_COLORS.length
  const color = AVATAR_COLORS[index]
  return color ?? AVATAR_COLORS[0]!
}

/**
 * Get initials from display name or username
 */
export function getInitials(displayName: string, username: string): string {
  const name = displayName.trim() || username.trim()

  // If display name has multiple words, use first letter of first two words
  const words = name.split(/\s+/).filter((word) => word.length > 0)
  if (words.length >= 2 && words[0] && words[1]) {
    const first = words[0][0]
    const second = words[1][0]
    if (first && second) {
      return (first + second).toUpperCase()
    }
  }

  // Otherwise, use first two letters of username/name
  return name.slice(0, 2).toUpperCase()
}

/**
 * Validate username format
 */
export function validateUsername(username: string): {
  valid: boolean
  error?: string
} {
  const trimmed = username.trim()

  if (!trimmed) {
    return { valid: false, error: 'Username is required' }
  }

  if (trimmed.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' }
  }

  if (trimmed.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' }
  }

  // Only letters, numbers, underscores
  const regex = /^[a-zA-Z0-9_]+$/
  if (!regex.test(trimmed)) {
    return {
      valid: false,
      error: 'Only letters, numbers, and underscores allowed',
    }
  }

  return { valid: true }
}
